import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import ToggleButton from "@mui/material/ToggleButton";
import Tooltip from "@mui/material/Tooltip";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import TitleIcon from "@mui/icons-material/Title";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import ToggleButtonGroup, {
	toggleButtonGroupClasses,
} from "@mui/material/ToggleButtonGroup";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
	[`& .${toggleButtonGroupClasses.grouped}`]: {
		margin: theme.spacing(0.4),
		border: 0,
		borderRadius: theme.shape.borderRadius,
	},
}));

export default function ToolbarTextEditor({ editor }) {
	const [emojiOpen, setEmojiOpen] = useState(false);

	const toggle = (cmd, value) => {
		editor
			.chain()
			.focus()
			[cmd](value ?? undefined)
			.run();
	};

	const isActive = (cmd, value) => editor?.isActive(cmd, value);

	return (
		<Paper
			variant="outlined"
			sx={{ display: "flex", flexWrap: "wrap" }}
		>
			<StyledToggleButtonGroup size="small">
				<Tooltip title="Emoji">
					<ToggleButton
						onClick={() => setEmojiOpen((prev) => !prev)}
						size="small"
						value="emoji"
					>
						<EmojiEmotionsIcon />
					</ToggleButton>
				</Tooltip>

				{emojiOpen && (
					<div
						style={{
							position: "absolute",
							zIndex: 10,
							top: "60px",
						}}
					>
						<Picker
							data={data}
							onEmojiSelect={(emoji) => {
								editor
									.chain()
									.focus()
									.insertContent(emoji.native)
									.run();
								setEmojiOpen(false);
							}}
							locale="fr"
						/>
					</div>
				)}
				<Tooltip title="Couleur du texte">
					<ToggleButton
						value="color"
						size="small"
					>
						<FormatColorTextIcon />
						<input
							type="color"
							onChange={(e) => {
								editor
									.chain()
									.focus()
									.setMark("textStyle", {
										color: e.target.value,
									})
									.run();
							}}
							style={{
								width: 30,
								height: 30,
								opacity: 0,
								position: "absolute",
								cursor: "pointer",
							}}
						/>
					</ToggleButton>
				</Tooltip>
				<Tooltip title="Gras">
					<ToggleButton
						value="bold"
						selected={isActive("bold")}
						onClick={() => toggle("toggleBold")}
						size="small"
					>
						<FormatBoldIcon />
					</ToggleButton>
				</Tooltip>
				<Tooltip title="Italique">
					<ToggleButton
						value="italic"
						selected={isActive("italic")}
						onClick={() => toggle("toggleItalic")}
						size="small"
					>
						<FormatItalicIcon />
					</ToggleButton>
				</Tooltip>
				<Tooltip title="Souligné">
					<ToggleButton
						value="underline"
						selected={isActive("underline")}
						onClick={() => toggle("toggleUnderline")}
						size="small"
					>
						<FormatUnderlinedIcon />
					</ToggleButton>
				</Tooltip>
			</StyledToggleButtonGroup>

			<Divider
				flexItem
				orientation="vertical"
				sx={{ mx: 1 }}
			/>

			<StyledToggleButtonGroup size="small">
				<Tooltip title="Aligner à gauche">
					<ToggleButton
						value="left"
						selected={editor?.isActive({ textAlign: "left" })}
						onClick={() => toggle("setTextAlign", "left")}
						size="small"
					>
						<FormatAlignLeftIcon />
					</ToggleButton>
				</Tooltip>
				<Tooltip title="Centrer">
					<ToggleButton
						value="center"
						selected={editor?.isActive({ textAlign: "center" })}
						onClick={() => toggle("setTextAlign", "center")}
						size="small"
					>
						<FormatAlignCenterIcon />
					</ToggleButton>
				</Tooltip>
				<Tooltip title="Aligner à droite">
					<ToggleButton
						value="right"
						selected={editor?.isActive({ textAlign: "right" })}
						onClick={() => toggle("setTextAlign", "right")}
						size="small"
					>
						<FormatAlignRightIcon />
					</ToggleButton>
				</Tooltip>
				<Tooltip title="Justifier">
					<ToggleButton
						value="justify"
						selected={editor?.isActive({ textAlign: "justify" })}
						onClick={() => toggle("setTextAlign", "justify")}
						size="small"
					>
						<FormatAlignJustifyIcon />
					</ToggleButton>
				</Tooltip>
			</StyledToggleButtonGroup>

			<Divider
				flexItem
				orientation="vertical"
				sx={{ mx: 1 }}
			/>

			<StyledToggleButtonGroup size="small">
				<Tooltip title="Titre 1">
					<ToggleButton
						value="h1"
						selected={isActive("heading", { level: 1 })}
						onClick={() => toggle("toggleHeading", { level: 1 })}
						size="small"
					>
						<TitleIcon fontSize="small" />1
					</ToggleButton>
				</Tooltip>
				<Tooltip title="Titre 2">
					<ToggleButton
						value="h2"
						selected={isActive("heading", { level: 2 })}
						onClick={() => toggle("toggleHeading", { level: 2 })}
						size="small"
					>
						<TitleIcon fontSize="small" />2
					</ToggleButton>
				</Tooltip>
				<Tooltip title="Paragraphe">
					<ToggleButton
						value="paragraph"
						selected={isActive("paragraph")}
						onClick={() => toggle("setParagraph")}
						size="small"
					>
						<TextFieldsIcon fontSize="small" />
					</ToggleButton>
				</Tooltip>
			</StyledToggleButtonGroup>
		</Paper>
	);
}

/*

/*
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import ImageIcon from "@mui/icons-material/Image";
import TitleIcon from "@mui/icons-material/Title";
import ToggleButton from "@mui/material/ToggleButton";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import ToggleButtonGroup, {
	toggleButtonGroupClasses,
} from "@mui/material/ToggleButtonGroup";
import { styled } from "@mui/material/styles";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import React, { useState } from "react";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
	[`& .${toggleButtonGroupClasses.grouped}`]: {
		margin: theme.spacing(0.5),
		border: 0,
		borderRadius: theme.shape.borderRadius,
		[`&.${toggleButtonGroupClasses.disabled}`]: {
			border: 0,
		},
	},
	[`& .${toggleButtonGroupClasses.middleButton},& .${toggleButtonGroupClasses.lastButton}`]:
		{
			marginLeft: -1,
			borderLeft: "1px solid transparent",
		},
}));

export default function ToolbarTextEditor({ editor }) {
	const [formats, setFormats] = useState([]);
	const [aligns, setAligns] = useState([]);
	const [showEmoji, setShowEmoji] = useState(false);
	const [color, setColor] = useState("#000000");
	const [fontSize, setFontSize] = useState("16px");

	const exec = (cmd, value) => editor?.chain().focus()[cmd](value).run();

	return (
		<Paper
			variant="outlined"
			sx={{
				display: "flex",
				flexWrap: "wrap",
			}}
		>
			{/* Bold / Italic / Underline 
			<StyledToggleButtonGroup
				size="small"
				value={formats}
				onChange={(_, v) => {
					setFormats(v);
					exec("toggleBold", v.includes("bold"));
					exec("toggleItalic", v.includes("italic"));
					exec("toggleUnderline", v.includes("underlined"));
				}}
			>
			
				<Tooltip title="Couleur du texte">
					<ToggleButton
						value="color"
						size="small"
					>
						<FormatColorTextIcon />
						<input
							type="color"
							value={color}
							onChange={(e) => {
								setColor(e.target.value);
								exec("setColor", e.target.value);
							}}
							style={{
								width: 30,
								height: 30,
								opacity: 0,
								position: "absolute",
								cursor: "pointer",
							}}
						/>
					</ToggleButton>
				</Tooltip>
				<Tooltip title="Gras">
					<ToggleButton value="bold">
						<FormatBoldIcon />
					</ToggleButton>
				</Tooltip>
				<Tooltip title="Italique">
					<ToggleButton value="italic">
						<FormatItalicIcon />
					</ToggleButton>
				</Tooltip>
				<Tooltip title="Souligné">
					<ToggleButton value="underlined">
						<FormatUnderlinedIcon />
					</ToggleButton>
				</Tooltip>
			</StyledToggleButtonGroup>

			<Divider
				flexItem
				orientation="vertical"
			/>

		
			<StyledToggleButtonGroup
				size="small"
				value={aligns}
				onChange={(_, v) => {
					setAligns(v);
					exec("setTextAlign", v.at(-1));
				}}
			>
				<Tooltip title="Aligner à gauche">
					<ToggleButton value="left">
						<FormatAlignLeftIcon />
					</ToggleButton>
				</Tooltip>
				<Tooltip title="Centrer">
					<ToggleButton value="center">
						<FormatAlignCenterIcon />
					</ToggleButton>
				</Tooltip>
				<Tooltip title="Aligner à droite">
					<ToggleButton value="right">
						<FormatAlignRightIcon />
					</ToggleButton>
				</Tooltip>
				<Tooltip title="Justifier">
					<ToggleButton value="justify">
						<FormatAlignJustifyIcon />
					</ToggleButton>
				</Tooltip>
			</StyledToggleButtonGroup>

			<Divider
				flexItem
				orientation="vertical"
			/>

		
			<Select
				sx={{ px: 1 }}
				value={fontSize}
				variant="standard"
				onChange={(e) => {
					setFontSize(e.target.value);
					exec("setFontSize", e.target.value);
				}}
			>
				{["12px", "14px", "16px", "20px", "24px", "32px"].map(
					(size) => (
						<MenuItem
							key={size}
							value={size}
						>
							{size}
						</MenuItem>
					)
				)}
			</Select>
			<Divider
				flexItem
				orientation="vertical"
			/>

		
			<StyledToggleButtonGroup size="small">
				<Tooltip title="Titre 1">
					<ToggleButton
						onClick={() => exec("toggleHeading", { level: 1 })}
					>
						<TitleIcon fontSize="small" />1
					</ToggleButton>
				</Tooltip>
				<Tooltip title="Titre 2">
					<ToggleButton
						onClick={() => exec("toggleHeading", { level: 2 })}
					>
						<TitleIcon fontSize="small" />2
					</ToggleButton>
				</Tooltip>
				<Tooltip title="Paragraphe">
					<ToggleButton onClick={() => exec("setParagraph")}>
						P
					</ToggleButton>
				</Tooltip>
			</StyledToggleButtonGroup>

		
			<StyledToggleButtonGroup size="small">
				<Tooltip title="Emoji">
					<ToggleButton onClick={() => setShowEmoji(!showEmoji)}>
						<InsertEmoticonIcon />
					</ToggleButton>
				</Tooltip>
			</StyledToggleButtonGroup>
			{showEmoji && (
				<div style={{ position: "absolute", zIndex: 999 }}>
					<Picker
						data={data}
						onEmojiSelect={(e) => {
							exec("insertContent", e.native);
							setShowEmoji(false);
						}}
					/>
				</div>
			)}
		</Paper>
	);
}
*/
